import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  Signal,
  computed,
  effect,
  inject,
} from '@angular/core';
import { FLOATING_PANEL_DATA } from '@shared/floating-panel/floating-panel-data.token';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdPanelSectionScroller } from '../bodygraph/hd-panel-section-scroller';
import { HdCircuitGateCardComponent } from '../circuit-gates/hd-circuit-gate-card.component';
import { HdCircuitGateSectionComponent } from '../circuit-gates/hd-circuit-gate-section.component';
import {
  HD_MEMORY_CIRCUITRY_ASSET,
  HD_MEMORY_CIRCUIT_GATES,
  HD_MEMORY_GATE_COLOR,
  HdMemoryPanelData,
} from './hd-memory.metadata';

interface MemoryPanelVm {
  readonly title: string;
  readonly circuitTitle: string;
  readonly circuitBullets: readonly string[];
  readonly jumpHint: string;
}

/**
 * Memory-circuit-paneel: bovenaan de algemene uitleg over het geheugencircuit
 * (de twee info-blokken van de cheat sheet), daaronder de twaalf memory-blokjes
 * naast het bodygraph-diagram (klikken springt naar de detailsectie), en tot
 * slot per poort een volledige {@link HdCircuitGateSectionComponent}. Geopend via
 * `HdMemoryPanelService`; een `focusGate` in de paneel-data laat het paneel na
 * render automatisch naar de betreffende poort scrollen (ook bij hergebruik van
 * een al open paneel).
 */
@Component({
  selector: 'hd-memory-circuit-panel',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HdCircuitGateCardComponent, HdCircuitGateSectionComponent],
  templateUrl: './hd-memory-circuit-panel.component.html',
  styleUrl: './hd-memory-circuit-panel.component.scss',
})
export class HdMemoryCircuitPanelComponent implements AfterViewChecked {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  private readonly panelData = inject(FLOATING_PANEL_DATA, { optional: true }) as
    | Signal<HdMemoryPanelData>
    | null;

  /** Auto-scroll naar de focus-poort, gedeeld met het voices-/angsten-paneel. */
  private readonly scroller = new HdPanelSectionScroller(this.host.nativeElement, this.cdr);

  protected readonly circuitryAsset = HD_MEMORY_CIRCUITRY_ASSET;
  protected readonly gates = HD_MEMORY_CIRCUIT_GATES;

  protected readonly vm = computed<MemoryPanelVm>(() => ({
    title: this.text('panelTitle'),
    circuitTitle: this.text('circuitInfo.title'),
    // De twee poster-blokken achter elkaar als één doorlopende opsomming.
    circuitBullets: [
      ...this.list('circuitInfo.left'),
      ...this.list('circuitInfo.right'),
    ],
    jumpHint: this.text('jumpHint'),
  }));

  constructor() {
    // Beide content-talen eager laden: taalwissel + NL-fallback zonder flikker.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');

    // Elke (her)aanlevering van paneel-data met een focus-poort wapent de
    // auto-scroll opnieuw; het scrollen zelf gebeurt in ngAfterViewChecked
    // zodra de sectie gerenderd is en de scope-tekst geladen is.
    effect(() => {
      const focusGate = this.panelData?.().focusGate ?? null;
      if (focusGate != null) {
        this.scroller.arm(
          `[data-section-gate="${focusGate}"]`,
          () => this.text(`gates.${focusGate}.keynote`) !== '',
        );
      }
    });

    this.destroyRef.onDestroy(() => this.scroller.dispose());
  }

  ngAfterViewChecked(): void {
    this.scroller.onViewChecked();
  }

  /** Railkleur van het blokje (en de sectie) van deze poort. */
  protected railFor(gate: number): string | undefined {
    return HD_MEMORY_GATE_COLOR[gate];
  }

  protected scrollToGate(gate: number): void {
    this.scroller.jump(`[data-section-gate="${gate}"]`);
  }

  /** Late afbeelding-load kan de layout verschuiven → extra view-check. */
  protected onDiagramLoad(): void {
    this.scroller.notifyLayoutShift();
  }

  private text(key: string): string {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetTextIn('memory', key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetTextIn('memory', key, 'nl');
  }

  private list(key: string): readonly string[] {
    const lang = this.contentLang.effectiveLang();
    const value = this.gateInfo.cheatSheetListIn('memory', key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cheatSheetListIn('memory', key, 'nl');
  }
}
